import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { Skeleton } from "@/components/Skeleton";
import { Button } from "@/components/Button";
import { Avatar } from "@/components/Avatar";
import { parseDateLocal } from "@/lib/format";
import type { AllocationRow } from "@shared/types";

const DAY = 86_400_000;

/**
 * Find allocation ids that, for a given person, overlap in time with others
 * such that the combined percentage exceeds 100 at some instant (a conflict).
 */
function conflictIds(allocs: AllocationRow[]): Set<string> {
  const out = new Set<string>();
  const byUser = new Map<string, AllocationRow[]>();
  for (const a of allocs) {
    const arr = byUser.get(a.userId) ?? [];
    arr.push(a);
    byUser.set(a.userId, arr);
  }
  for (const arr of byUser.values()) {
    // Sweep over each allocation's boundaries; at each start, sum the percent
    // of every allocation whose range covers that instant.
    for (const probe of arr) {
      const t = parseDateLocal(probe.startDate)!.getTime();
      let sum = 0;
      const covering: string[] = [];
      for (const a of arr) {
        const s = parseDateLocal(a.startDate)!.getTime();
        const e = parseDateLocal(a.endDate)!.getTime();
        if (s <= t && t <= e) {
          sum += a.percent;
          covering.push(a.id);
        }
      }
      if (sum > 100) covering.forEach((id) => out.add(id));
    }
  }
  return out;
}

function AllocationsView() {
  const qc = useQueryClient();
  const allocQ = useQuery({
    queryKey: ["allocations"],
    queryFn: api.listAllocations,
  });
  const projectsQ = useQuery({
    queryKey: queryKeys.projects(),
    queryFn: api.listProjects,
  });
  const meQ = useQuery({ queryKey: queryKeys.me(), queryFn: api.me });
  const teamQ = useQuery({ queryKey: queryKeys.team(), queryFn: api.listTeam });

  const allocs = useMemo(() => allocQ.data?.allocations ?? [], [allocQ.data]);
  const conflicts = useMemo(() => conflictIds(allocs), [allocs]);

  // Projects the signed-in user owns (can allocate against).
  const myId = meQ.data?.user?.id;
  const ownedProjects = (projectsQ.data?.projects ?? []).filter(
    (p) => p.ownerId === myId,
  );

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteAllocation(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["allocations"] }),
  });

  // Timeline range.
  const { start, totalDays } = useMemo(() => {
    const today = Math.floor(Date.now() / DAY);
    const nums = [today, today + 60];
    for (const a of allocs) {
      nums.push(Math.floor(parseDateLocal(a.startDate)!.getTime() / DAY));
      nums.push(Math.floor(parseDateLocal(a.endDate)!.getTime() / DAY));
    }
    const min = Math.min(...nums) - 3;
    const max = Math.max(...nums) + 3;
    return { start: min, totalDays: max - min + 1 };
  }, [allocs]);
  const pxPerDay = Math.max(2, Math.min(8, 720 / totalDays));
  const dayOf = (s: string) =>
    Math.floor(parseDateLocal(s)!.getTime() / DAY) - start;

  const byUser = useMemo(() => {
    const m = new Map<string, AllocationRow[]>();
    for (const a of allocs) {
      const arr = m.get(a.userId) ?? [];
      arr.push(a);
      m.set(a.userId, arr);
    }
    return [...m.entries()];
  }, [allocs]);

  return (
    <div className="pg-wrap" style={{ padding: "22px 30px 60px" }}>
      <h1 className="pg-title" style={{ margin: 0 }}>
        Resource allocation
      </h1>
      <p className="t-sm t-muted">
        Allocate members a % of their time to a project for a date range. Bars
        that overlap above 100% for a person are flagged as conflicts (red).
      </p>

      {ownedProjects.length > 0 && (
        <AllocateForm
          projects={ownedProjects.map((p) => ({ id: p.id, name: p.name }))}
          members={(teamQ.data?.members ?? []).map((m) => ({
            id: m.id,
            name: m.name ?? m.email ?? m.id.slice(0, 8),
          }))}
          onCreated={() => qc.invalidateQueries({ queryKey: ["allocations"] })}
        />
      )}

      {allocQ.isLoading ? (
        <Skeleton count={4} height={40} />
      ) : byUser.length === 0 ? (
        <p className="t-muted" style={{ marginTop: 16 }}>
          No allocations yet.
        </p>
      ) : (
        <div className="alloc-tl" style={{ marginTop: 18 }}>
          {byUser.map(([userId, arr]) => (
            <div key={userId} className="alloc-person">
              <div className="alloc-person-head">
                <Avatar
                  id={userId}
                  name={arr[0]!.userName ?? userId}
                  size={22}
                />
                <span>{arr[0]!.userName ?? userId}</span>
              </div>
              <div
                className="alloc-track"
                style={{ width: totalDays * pxPerDay }}
              >
                {arr.map((a) => {
                  const left = dayOf(a.startDate) * pxPerDay;
                  const w = Math.max(
                    16,
                    (dayOf(a.endDate) - dayOf(a.startDate) + 1) * pxPerDay,
                  );
                  const bad = conflicts.has(a.id);
                  return (
                    <div
                      key={a.id}
                      className={`alloc-bar${bad ? " conflict" : ""}`}
                      style={{ left, width: w }}
                      title={`${a.projectName} — ${a.percent}% (${a.startDate} → ${a.endDate})${bad ? " — CONFLICT >100%" : ""}`}
                    >
                      <span className="alloc-bar-label">
                        {a.projectName} {a.percent}%
                      </span>
                      <button
                        type="button"
                        className="alloc-del"
                        aria-label={`Remove allocation ${a.projectName}`}
                        onClick={() => remove.mutate(a.id)}
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AllocateForm({
  projects,
  members,
  onCreated,
}: {
  projects: { id: string; name: string }[];
  members: { id: string; name: string }[];
  onCreated: () => void;
}) {
  const [projectId, setProjectId] = useState(projects[0]?.id ?? "");
  const [userId, setUserId] = useState(members[0]?.id ?? "");
  const [percent, setPercent] = useState("50");
  const [startDate, setStart] = useState("");
  const [endDate, setEnd] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.createAllocation(projectId, {
        userId,
        percent: Number(percent),
        startDate,
        endDate,
      }),
    onSuccess: () => {
      setErr(null);
      setStart("");
      setEnd("");
      onCreated();
    },
    onError: (e: Error) => setErr(e.message),
  });

  const valid =
    projectId && userId && Number(percent) >= 1 && startDate && endDate;

  return (
    <div className="alloc-form">
      <select
        className="select"
        value={projectId}
        onChange={(e) => setProjectId(e.target.value)}
        aria-label="Allocate to project"
      >
        {projects.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>
      <select
        className="select"
        value={userId}
        onChange={(e) => setUserId(e.target.value)}
        aria-label="Allocate member"
      >
        {members.map((m) => (
          <option key={m.id} value={m.id}>
            {m.name}
          </option>
        ))}
      </select>
      <input
        className="input"
        type="number"
        min="1"
        max="100"
        value={percent}
        onChange={(e) => setPercent(e.target.value)}
        aria-label="Allocation percent"
        style={{ width: 80 }}
      />
      <input
        className="input"
        type="date"
        value={startDate}
        onChange={(e) => setStart(e.target.value)}
        aria-label="Allocation start"
      />
      <input
        className="input"
        type="date"
        value={endDate}
        onChange={(e) => setEnd(e.target.value)}
        aria-label="Allocation end"
      />
      <Button
        variant="primary"
        disabled={!valid || create.isPending}
        onClick={() => create.mutate()}
      >
        Allocate
      </Button>
      {err && <span className="t-danger t-sm">{err}</span>}
    </div>
  );
}

export const Route = createFileRoute("/allocations")({
  component: AllocationsView,
});
